import type { FC, ReactNode } from 'react';

import type { ItemDecision, ItemState } from '@eluve/api-contract';
import { Button, Icon, Stack, Tag, cn, tv } from '@eluve/components';
import type { TagProps } from '@eluve/components';
import { type MessageKey, useTranslation } from '@eluve/i18n';

const rowStyles = tv({
  base: 'w-full rounded-lg border p-3.5 shadow-sm transition-all',
  variants: {
    decision: {
      approved: 'border-border-positive-subtle bg-bg-positive-subtle',
      edited: 'border-border-insights-subtle bg-bg-insights-subtle',
      rejected:
        'border-border-negative-subtle bg-bg-negative-subtle opacity-55',
      pending: 'border-border-secondary bg-bg-primary',
    },
  },
});

const DECISION_TAG: Record<
  'approved' | 'edited' | 'rejected',
  { label: MessageKey; color: TagProps['color'] }
> = {
  approved: { label: 'carePlans.review.confirmed', color: 'positive' },
  edited: { label: 'carePlans.review.edited', color: 'insights' },
  rejected: { label: 'carePlans.review.rejected', color: 'negative' },
};

export const ItemReviewRow: FC<{
  itemState: ItemState;
  decision: ItemDecision;
  title: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
  onConfirm?: () => void;
  onReject?: () => void;
  onUndo?: () => void;
  onEdit?: () => void;
  sourceLabel?: string;
  onSelectSource?: (triggerElement: HTMLElement) => void;
}> = ({
  itemState,
  decision,
  title,
  children,
  disabled,
  onConfirm,
  onReject,
  onUndo,
  onEdit,
  sourceLabel,
  onSelectSource,
}) => {
  const { t } = useTranslation();
  const reviewable = itemState !== 'unchanged';
  const struck = reviewable && decision === 'rejected';

  return (
    <div className={rowStyles({ decision: reviewable ? decision : 'pending' })}>
      <Stack
        direction="horizontal"
        align="center"
        justify="between"
        gap={3}
        wrap
      >
        {sourceLabel && onSelectSource ? (
          <div
            role="button"
            tabIndex={0}
            aria-label={`Open evidence for ${sourceLabel}`}
            className={cn(
              'min-w-0 flex-1 cursor-pointer rounded-md text-left text-sm font-medium outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
              struck && 'line-through',
            )}
            onClick={(event) => onSelectSource(event.currentTarget)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelectSource(event.currentTarget);
              }
            }}
          >
            {title}
          </div>
        ) : (
          <div
            className={cn(
              'min-w-0 flex-1 text-sm font-medium',
              struck && 'line-through',
            )}
          >
            {title}
          </div>
        )}
        <Stack
          direction="horizontal"
          align="center"
          gap={1}
          fullWidth={false}
          className="shrink-0"
        >
          {sourceLabel && onSelectSource && (
            <Button
              size="sm"
              variant="ghost"
              aria-label={`View source for ${sourceLabel}`}
              onClick={(event) => onSelectSource(event.currentTarget)}
            >
              <Icon name="FileText" size="auto" />
            </Button>
          )}
          {/* A decided, reviewable row shows its verdict before row actions. */}
          {reviewable && decision !== 'pending' && (
            <Tag
              size="s"
              subtle={false}
              color={DECISION_TAG[decision].color}
              label={t(DECISION_TAG[decision].label)}
              className="h-[30px] rounded-lg px-3"
            />
          )}
          {/* Confirm/Reject only while a reviewable row is still pending. */}
          {reviewable && decision === 'pending' && (
            <Button
              size="sm"
              variant="outline"
              disabled={disabled || !onConfirm}
              className="border-border-positive-subtle bg-bg-positive-subtle text-text-positive hover:bg-bg-positive-subtle"
              onClick={onConfirm}
            >
              <Icon name="Check" size="auto" />
              {t('common.confirm')}
            </Button>
          )}
          {/* Edit is always available — a decided or unchanged item can still be
              corrected (design: pencil present in every state). */}
          <Button
            size="sm"
            variant="outline"
            disabled={disabled || !onEdit}
            onClick={onEdit}
            aria-label={t('carePlans.review.edit')}
          >
            <Icon name="Pencil" size="auto" />
          </Button>
          {reviewable && decision === 'pending' && (
            <Button
              size="sm"
              variant="outline"
              disabled={disabled || !onReject}
              className="border-border-negative-subtle bg-bg-negative-subtle text-text-negative hover:bg-bg-negative-subtle"
              onClick={onReject}
              aria-label={t('carePlans.review.reject')}
            >
              <Icon name="X" size="auto" />
            </Button>
          )}
          {reviewable && decision !== 'pending' && (
            <Button
              size="sm"
              variant="ghost"
              disabled={disabled || !onUndo}
              onClick={onUndo}
            >
              <Icon name="Undo2" size="auto" />
              {t('carePlans.review.undo')}
            </Button>
          )}
        </Stack>
      </Stack>
      {children}
    </div>
  );
};
